import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'

import { ApiError } from '@/api/client'
import { roomBookingsApi } from '@/api/endpoints'
import type { RoomBooking } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { canManageRoomBookings } from '@/auth/permissions'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog, Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { ETYKIETY_REZERWACJI } from '@/lib/roomBookings'
import { formatTime } from '@/lib/scheduleTime'

/**
 * Szczegóły zajętej sali i jedyna operacja, jaka ma tu sens: zwolnienie.
 *
 * Rezerwacji się nie edytuje — inna godzina to po prostu inna rezerwacja, a
 * zwolnienie i założenie od nowa jest krótsze niż formularz zmiany. Rezerwacji
 * się też nie odwołuje: odwołane zajęcia zostają w historii, bo ktoś na nie
 * czekał, a odwołany remont po prostu się nie odbył.
 */
export function RoomBookingDetailModal({
  booking,
  onClose,
}: {
  booking: RoomBooking | null
  onClose: () => void
}) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { notifySuccess } = useToast()
  const [potwierdzenie, setPotwierdzenie] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => roomBookingsApi.remove(booking?.id ?? ''),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['room-bookings'] })
      await queryClient.invalidateQueries({ queryKey: ['schedule'] })
      notifySuccess('Sala jest znowu wolna.')
      setPotwierdzenie(false)
      onClose()
    },
    onError: (caught: unknown) => {
      setPotwierdzenie(false)
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się zwolnić sali.')
    },
  })

  if (!booking) return null

  const dzien = new Date(booking.starts_at).toLocaleDateString('pl-PL', { dateStyle: 'full' })

  return (
    <>
      <Modal
        open={booking !== null}
        onClose={onClose}
        title="Rezerwacja sali"
        description={`${booking.room.name} — ${booking.location.name}`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Zamknij
            </Button>
            {canManageRoomBookings(user) && (
              <Button
                variant="danger"
                icon={<Trash2 size={16} />}
                onClick={() => setPotwierdzenie(true)}
              >
                Zwolnij salę
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-4 text-sm">
          {error && <Alert tone="error">{error}</Alert>}

          <Pozycja etykieta="Termin">
            {dzien}, {formatTime(booking.starts_at)}–{formatTime(booking.ends_at)}
          </Pozycja>
          <Pozycja etykieta="Powód">{ETYKIETY_REZERWACJI[booking.kind]}</Pozycja>
          {booking.note && <Pozycja etykieta="Opis">{booking.note}</Pozycja>}
          {booking.created_by && (
            <Pozycja etykieta="Zarezerwował">{booking.created_by.full_name}</Pozycja>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={potwierdzenie}
        title="Zwolnić salę?"
        message={`Rezerwacja ${dzien} ${formatTime(booking.starts_at)}–${formatTime(booking.ends_at)} zniknie z grafiku. Od tej chwili będzie można wpisać w ten termin zajęcia.`}
        confirmLabel="Zwolnij salę"
        isLoading={mutation.isPending}
        onConfirm={() => mutation.mutate()}
        onClose={() => setPotwierdzenie(false)}
      />
    </>
  )
}

function Pozycja({ etykieta, children }: { etykieta: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
        {etykieta}
      </p>
      <p className="text-on-surface">{children}</p>
    </div>
  )
}
